"use client";
import { useCallback } from "react";
import useSWR from "swr";
import type { CategoryItem } from "@/types";
import { cfApi, cfFetcher, cfFetch } from "../api/apiClient";

function parseApiError(body: unknown): string {
  const b = body as { detail?: { error?: string } | string };
  const d = b.detail;
  return typeof d === "string"
    ? d
    : typeof d === "object" && d?.error
      ? d.error
      : "Request failed";
}

export function useCategories() {
  const { data, error, isLoading, mutate } = useSWR<CategoryItem[]>(
    cfApi("/categories"),
    cfFetcher,
  );

  const createCategory = useCallback(
    async (
      name: string,
      color: string,
      type: "income" | "expense",
      parentCategoryId?: string | null,
    ): Promise<CategoryItem> => {
      const res = await cfFetch("/categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          color,
          type,
          parentCategoryId: parentCategoryId ?? null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(parseApiError(body));
      }
      const created = (await res.json()) as CategoryItem;
      await mutate();
      return created;
    },
    [mutate],
  );

  const reorderCategories = useCallback(
    async (orderedCategoryIds: string[]): Promise<void> => {
      const res = await cfFetch("/categories", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderedCategoryIds }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(parseApiError(body));
      }
      await mutate();
    },
    [mutate],
  );

  const deleteCategory = useCallback(
    async (categoryId: string): Promise<void> => {
      const res = await cfFetch(`/categories/${categoryId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(parseApiError(body));
      }
      await mutate();
    },
    [mutate],
  );

  return {
    categories: data ?? [],
    isLoading,
    error,
    mutate,
    createCategory,
    reorderCategories,
    deleteCategory,
  };
}
